import type { ReactNode } from 'react';
import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router';
import {
    ArrowUpRight,
    ChevronLeft,
    ChevronRight,
    Minus,
    Plus,
    Share,
    X,
} from 'lucide-react';

import { cn } from '@rocco/utils/cn';
import { useWebsiteVisitAction } from '~/modules/tracking/hook/use-website-visit-action';
import type { TileVisitWebsiteConfig } from '~/modules/moodboards/components/TileActionPanel';
import { useAppDisplayModeContext } from '@archipro-design/aria';
import { useFollowSelectorProxy } from '~/modules/moodboards/hooks';
import type { PinItemType } from '~/modules/moodboards/type';
import { useBoardsService } from '~/modules/moodboards/hooks/use-boards-service';
import { ShareSheet } from '~/modules/moodboards/components/ShareSheet';
import {
    isNativeShareAvailable,
    shareNative,
    type ShareOptions,
} from '~/modules/social-sharing/utils/sharing';

/** One frame of the overlay: everything it needs to draw a slide. */
export interface OverlaySlide {
    src: string;
    alt: string;
    /**
     * The library image behind the picture, where the surface knows it. Lets a
     * caller elsewhere on the page — a page-builder module drawing the same
     * photograph — ask the viewer to open on this slide.
     */
    id?: number;
    /** The description the photograph carries in the page builder, if any. */
    caption?: string;
    /**
     * Who made the thing in the picture. Either line may carry a link — the
     * project and the practice are both places a reader may want to go from
     * here, so neither is decoration.
     */
    credit?: {
        title: string;
        titleLink?: string;
        subtitle?: string;
        subtitleLink?: string;
    };
    /**
     * A clear-cut render — a product cut out onto a white ground — rather than
     * a photograph. It needs the neutral fill and the multiply the carousel
     * gives it, or a white-on-white product reads as nothing at all.
     */
    multiplied?: boolean;
    /**
     * A player to draw in place of the picture, where the slide is a video.
     *
     * Handed in already built rather than named by URL: this viewer is shared
     * by the product, project and professional pages, and how a video plays is
     * the surface's business, not the viewer's. `src` still carries the
     * poster, so anything reading a slide's picture keeps working.
     */
    video?: ReactNode;
    /** Products tagged into this photograph, as cards beside it. */
    tags?: OverlayTag[];
    /**
     * Where the photograph itself leads. Set on surfaces where the viewer is
     * a way in rather than the destination — the Spaces wall shows one photo
     * out of a project, and the picture is the thing a reader clicks. Left
     * unset on a detail page, where the reader is already there.
     */
    href?: string;
}

/** One tagged product on a slide. */
export interface OverlayTag {
    id: number;
    title: string;
    professional: string;
    link: string;
    imageUrl?: string;
    /**
     * Where the product sits in the photograph, as percentages of the image —
     * 50/50 is its centre, the same frame the tagging tool records in. Absent
     * on a tag placed before positions were kept, in which case hovering its
     * card simply marks nothing.
     */
    positionX?: number;
    positionY?: number;
}

/** The ground every viewer sits on: darkened and blurred. */
export const OVERLAY_GROUND = 'bg-black/75 backdrop-blur-2xl';

/**
 * Whether a key press belongs to something the reader is typing into, so the
 * viewer's single-key shortcuts leave it alone. A board named "Kitchen - Phase
 * 2" carries a `-`, and the design-board picker's search and naming fields both
 * live inside the viewer.
 */
const isTypingTarget = (target: EventTarget | null): boolean => {
    if (!(target instanceof HTMLElement)) return false;
    return (
        target.isContentEditable ||
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.tagName === 'SELECT'
    );
};

/** Everything inside the viewer a Tab can land on, in document order. */
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');

/**
 * The viewer's tab stops as the reader meets them: laid out and on screen, so
 * the controls a breakpoint hides — the board pill on a phone — are not tabbed
 * to from a screen that does not draw them.
 */
const tabStopsIn = (dialog: HTMLElement): HTMLElement[] =>
    [...dialog.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)].filter(
        (node) => node.getClientRects().length > 0,
    );

/** The row the close control and the actions sit in. */
export const OVERLAY_TOP_BAR =
    'flex items-center justify-between max-md:px-4 max-md:py-3 md:px-[1.5rem] md:py-[1.25rem]';

const ZOOM_STEP = 0.5;
const ZOOM_MIN = 1;
const ZOOM_MAX = 3;

/** A drag across the phone's photograph turns the page past this travel. */
const SWIPE_MIN = 40;
/** A pointer that moves less than this between down and up was a tap. */
const TAP_MAX = 8;
/** The share of the picture's width at each side that a tap treats as an edge. */
const EDGE = 0.1;

/**
 * The overlay's chrome: a translucent disc or pill on the blurred ground.
 *
 * Exported because the article's modal is the same viewer around different
 * content, and two definitions of the same control drift apart.
 */
export const CHROME = cn(
    'inline-flex cursor-pointer items-center justify-center rounded-[1.375rem]',
    'font-sans font-medium text-white transition-colors',
    'bg-white/15 backdrop-blur-sm hover:bg-white/25',
);

/**
 * A true circle, and without the pills' own blur: the ground under it is
 * already blurred, and blurring it again drew a dark rim round the edge.
 */
export const DISC = cn(
    CHROME,
    'size-[3rem] shrink-0 rounded-full backdrop-blur-none',
);

/** Where the two paging arrows sit: outside the content, mid-height. */
export const OVERLAY_ARROW_SLOT = 'absolute top-1/2 z-10 -translate-y-1/2';

export const PILL = cn(
    CHROME,
    'h-[3rem] gap-[0.5rem] px-[1.25rem] text-[1rem]',
);

/** Close and zoom read as solid controls rather than scrim, like Save. */
export const WHITE_DISC = cn(
    'inline-flex size-[3rem] shrink-0 cursor-pointer items-center justify-center rounded-[1.375rem]',
    'bg-white text-[#181818] transition-colors hover:bg-white/85',
);

interface DetailImageOverlayProps {
    slides: OverlaySlide[];
    /**
     * Where the image sat on the page when it was clicked. The overlay opens
     * from there rather than appearing at full size — omit it and the image
     * simply fades up in place.
     */
    originRect?: DOMRect | null;
    index: number;
    onIndexChange: (next: number) => void;
    onClose: () => void;
    /**
     * The page the viewer was opened from, for the design-board picker: what
     * a save pins when the photograph on screen has no library image behind
     * it (a video slide, a surface that never resolved one). A slide that
     * knows its own image is pinned as that image instead — see the pin
     * derivation in the body.
     */
    pinId: number;
    pinType?: PinItemType;
    title: string;
    /**
     * The supplier's site, for the viewer's bottom-left "Visit site". Omit it
     * and the corner stays empty — a surface with no website to offer draws
     * nothing rather than a dead control.
     */
    visitWebsite?: TileVisitWebsiteConfig | null;
}

/**
 * One line of the credit — a link when there is somewhere to go, plain text
 * otherwise. Never underlined: at this size a rule under the words would read
 * as a second element rather than as a link, so the hover fade carries it.
 */
const CreditLine = ({
    text,
    href,
    className,
}: {
    text: string;
    href?: string;
    className: string;
}) =>
    href ? (
        <Link
            to={href}
            className={cn(
                className,
                'no-underline transition-opacity hover:opacity-70',
            )}
        >
            {text}
        </Link>
    ) : (
        <span className={className}>{text}</span>
    );

/**
 * The full-screen image viewer: the photograph fitted to the screen on a
 * blacked out, blurred page, with the chrome floating over it — close at the
 * left of the top bar, the credit in its middle, share / board / save at the
 * right, the two arrows on the screen's edges, and zoom at the foot.
 *
 * Deliberately not `PhotoViewerModal`: that one carries a magnifier and an
 * enquiry footer, and this is meant to be the image and nothing else.
 */
export const DetailImageOverlay = ({
    slides,
    originRect,
    index,
    onIndexChange,
    onClose,
    pinId,
    pinType = 'ProductPin',
    title,
    visitWebsite,
}: DetailImageOverlayProps) => {
    /*
     * A null `website` makes the hook return a null `websiteURL`, which is the
     * cue to draw nothing — so the zeroed attribution below is never the thing
     * reported, it only keeps the hook's shape while there is no site to visit.
     */
    const { websiteURL, visitWebsite: onVisitWebsite } = useWebsiteVisitAction({
        website: visitWebsite?.website,
        libraryItemID: visitWebsite?.libraryItemID ?? 0,
        professionalID: visitWebsite?.professionalID ?? 0,
        professionalTitle: visitWebsite?.professionalTitle ?? '',
        source: 'ImageViewerWebsite',
    });
    const [zoom, setZoom] = useState(ZOOM_MIN);
    const { mobile } = useAppDisplayModeContext();
    const stageRef = useRef<HTMLDivElement>(null);
    const dialogRef = useRef<HTMLDivElement>(null);

    /**
     * The opening move, FLIP-style: the stage is placed over the thumbnail the
     * reader clicked, then released to its own position on the next frame so
     * the browser animates the difference. The chrome fades up alongside it.
     *
     * One uniform scale, not separate x and y: the frame on the page is square
     * and the stage is not, so scaling each axis to fit would visibly squash
     * the photograph on the way in.
     */
    const [entering, setEntering] = useState(Boolean(originRect));
    /**
     * The tag card the reader is pointing at, so the photograph can show them
     * where in it that product actually is. Held by id rather than by object:
     * the slide's tag list is rebuilt on every render.
     */
    const [hoveredTagId, setHoveredTagId] = useState<number | null>(null);

    /**
     * Where the reader has dragged the zoomed photograph to.
     *
     * Zooming without panning shows them the middle of the image at a larger
     * size and nothing else, which is not what zooming is for. Held in state
     * rather than written to the node because the same transform carries the
     * zoom, and the two have to be composed.
     */
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragFrom = useRef<{ x: number; y: number } | null>(null);
    const canPan = zoom > ZOOM_MIN;

    // Back to the middle whenever there is nothing left to pan around — a
    // stale offset would leave the next slide, or the un-zoomed view, sitting
    // off-centre for no reason the reader can see.
    useEffect(() => {
        if (!canPan) setPan({ x: 0, y: 0 });
    }, [canPan]);
    useEffect(() => {
        setPan({ x: 0, y: 0 });
    }, [index]);

    /**
     * On a phone the photograph itself is the control: a drag across it
     * turns the page, and a tap in either edge of the picture does the
     * same, one image at a time. Both are read from the pointer's travel
     * between down and up, and both must then swallow the click the browser
     * fires after it — the picture may be a link to its own page, and a
     * reader turning the page has not asked to leave the viewer.
     */
    const swipeFrom = useRef<{ x: number; y: number } | null>(null);
    const swallowClick = useRef(false);
    const canTurn = mobile && !canPan && slides.length > 1;

    const turnFrom = (event: React.PointerEvent) => {
        const from = swipeFrom.current;
        swipeFrom.current = null;
        if (!from) return false;
        const dx = event.clientX - from.x;
        const dy = event.clientY - from.y;
        if (Math.abs(dx) > SWIPE_MIN && Math.abs(dx) > Math.abs(dy)) {
            step(dx < 0 ? 1 : -1);
            return true;
        }
        if (isVideo || Math.abs(dx) > TAP_MAX || Math.abs(dy) > TAP_MAX) {
            return false;
        }
        const picture = stageRef.current?.querySelector('img');
        if (!picture) return false;
        const rect = picture.getBoundingClientRect();
        const at = (event.clientX - rect.left) / rect.width;
        if (at < 0 || at > 1) return false;
        if (at <= EDGE) {
            step(-1);
            return true;
        }
        if (at >= 1 - EDGE) {
            step(1);
            return true;
        }
        return false;
    };

    const onPanStart = (event: React.PointerEvent) => {
        // A second finger is a pinch, not a swipe. Without this the pinch
        // overwrote the start point and the first finger's release measured
        // the distance between two different fingers, which clears SWIPE_MIN
        // easily and turned the page instead of zooming.
        if (!event.isPrimary) return;
        // Whatever the last gesture left behind, this one starts clean. A
        // swipe that ends off the stage releases somewhere else, so its
        // `pointerup` never reaches these handlers and its start point would
        // otherwise still be sitting here when the next gesture reads it.
        // (Capturing the pointer would also fix that, but capture retargets
        // the click that follows, and a tap on the picture has to reach the
        // link underneath it.)
        swipeFrom.current = null;
        dragFrom.current = null;
        swallowClick.current = false;
        if (canTurn) {
            swipeFrom.current = { x: event.clientX, y: event.clientY };
            return;
        }
        if (!canPan) return;
        dragFrom.current = {
            x: event.clientX - pan.x,
            y: event.clientY - pan.y,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
    };
    const onPanMove = (event: React.PointerEvent) => {
        const from = dragFrom.current;
        if (!from) return;
        setPan({ x: event.clientX - from.x, y: event.clientY - from.y });
    };
    const onPanEnd = (event: React.PointerEvent) => {
        if (swipeFrom.current && turnFrom(event)) {
            swallowClick.current = true;
        }
        // Both, every time. Returning early out of the swipe branch left a
        // pan's start point and its pointer capture in place, so a zoomed
        // picture went on panning with no button held.
        swipeFrom.current = null;
        dragFrom.current = null;
        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };
    const onStageClickCapture = (event: React.MouseEvent) => {
        if (!swallowClick.current) return;
        swallowClick.current = false;
        event.preventDefault();
        event.stopPropagation();
    };
    const [fromTransform, setFromTransform] = useState<string>();

    useLayoutEffect(() => {
        const stage = stageRef.current;
        if (!stage || !originRect) {
            setEntering(false);
            return;
        }
        const to = stage.getBoundingClientRect();
        if (!to.width || !to.height) {
            setEntering(false);
            return;
        }
        const scale = originRect.width / to.width;
        const dx =
            originRect.left + originRect.width / 2 - (to.left + to.width / 2);
        const dy =
            originRect.top + originRect.height / 2 - (to.top + to.height / 2);
        setFromTransform(`translate(${dx}px, ${dy}px) scale(${scale})`);

        // Two frames: one for the start transform to be painted, the next to
        // change it — a single frame can be coalesced into no transition.
        const raf = requestAnimationFrame(() =>
            requestAnimationFrame(() => setEntering(false)),
        );
        return () => cancelAnimationFrame(raf);
        // Only the rect the overlay opened from matters; it does not change.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Each photograph's own aspect, by src, measured once it has loaded.
     *
     * The stage stretches the picture's box in both axes, so `max-width` and
     * `max-height` bind independently and the box comes out a different shape
     * from the photograph. `object-contain` then letterboxes inside it, which
     * left the rounded corners clipping transparent margin instead of the
     * picture, and a tag mark placed by percentage landing in that margin.
     * Pinning the box to this ratio makes box and photograph one rectangle
     * again, and both follow.
     *
     * Kept for every photograph seen, not just the one on screen: a src with
     * a ratio is one the browser already holds decoded, which is what lets the
     * viewer turn the page without the picture blanking — see the held slide
     * below. Zero records a picture that failed to load, so it still shows.
     */
    const [ratios, setRatios] = useState<Record<string, number>>({});
    const imageRef = useRef<HTMLImageElement>(null);
    const currentSrc = slides[index]?.src;

    const record = useCallback((src: string, image: HTMLImageElement) => {
        const value =
            image.naturalWidth && image.naturalHeight
                ? image.naturalWidth / image.naturalHeight
                : 0;
        setRatios((previous) =>
            previous[src] === value ? previous : { ...previous, [src]: value },
        );
    }, []);

    const measure = (image: HTMLImageElement) =>
        record(image.getAttribute('src') ?? '', image);

    // A cached image is already complete when it mounts, so its load event has
    // been and gone — measure it here instead, or it would keep the stretched
    // box until something else happened to re-render.
    useEffect(() => {
        const image = imageRef.current;
        if (image?.complete) measure(image);
        // `measure` closes over nothing that changes between slides.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentSrc]);

    /**
     * The photograph asked for and the ones either side of it, fetched and
     * decoded off-screen, so an arrow press finds the next one ready rather
     * than drawing it as it arrives. Keyed by the srcs as a string: the
     * surfaces rebuild their slide arrays on every render.
     */
    const started = useRef(new Set<string>());
    const nearbySrcs = [0, 1, -1]
        .map(
            (offset) =>
                slides[(index + offset + slides.length) % slides.length],
        )
        .map((nearby) => (nearby && !nearby.video ? nearby.src : ''))
        .join('\n');

    useEffect(() => {
        for (const src of nearbySrcs.split('\n')) {
            if (!src || started.current.has(src)) continue;
            started.current.add(src);
            const image = new Image();
            image.src = src;
            void image
                .decode()
                .catch(() => undefined)
                .then(() => record(src, image));
        }
    }, [nearbySrcs, record]);

    /**
     * What the viewer draws: the slide asked for once its photograph is ready,
     * and the last ready one until then. Swapping in a picture that has not
     * arrived blanked the stage and drew the file at its own size before
     * fitting it — the flash on every page turn, and again when a surface
     * swaps its slides for the same photographs at other URLs.
     */
    const target = slides[index];
    const targetReady =
        !target || Boolean(target.video) || target.src in ratios;
    const [held, setHeld] = useState(target);
    useEffect(() => {
        if (targetReady) setHeld(target);
    }, [target, targetReady]);

    const { boards, initialFetch } = useBoardsService();
    /**
     * What a save from here pins: the photograph on screen, where the surface
     * knows the library image behind it, and the page it was opened from
     * otherwise.
     *
     * A reader who opens the viewer and saves is taking that picture — the
     * page has its own Save in the CTA cluster, and pinning the page from
     * here made every photograph in a gallery save the same thing. Read off
     * the current slide rather than fixed for the viewer, so paging on moves
     * the target with the reader; the page stays the library item either way,
     * which is what a board pin is filed under.
     */
    const slidePinId = slides[index]?.id;
    const {
        followSelector,
        openFollowSelector,
        isOpen: pickerOpen,
    } = useFollowSelectorProxy({
        pinId: slidePinId ?? pinId,
        pinType: slidePinId ? 'PhotoPin' : pinType,
        libraryItemId: pinId,
    });

    // Boards are only named on the chip, so they are asked for when the
    // overlay opens rather than with the page.
    useEffect(() => {
        initialFetch();
    }, [initialFetch]);

    // Most recently touched board — the service sorts by LastActivity DESC —
    // which is the one a save would most likely be headed for.
    const boardName = boards[0]?.title ?? 'Design board';

    // A player is not a photograph: there are no pixels to magnify, and a drag
    // across it would fight the controls sitting on top of it. The zoom stays
    // at rest, which also leaves `canPan` false and the pan handlers idle.
    const isVideo = Boolean(slides[index]?.video);
    const zoomBy = useCallback(
        (delta: number) => {
            if (isVideo) return;
            setZoom((z) => Math.min(ZOOM_MAX, Math.max(ZOOM_MIN, z + delta)));
        },
        [isVideo],
    );

    const step = useCallback(
        (delta: number) => {
            if (slides.length < 2) return;
            setZoom(ZOOM_MIN);
            onIndexChange((index + delta + slides.length) % slides.length);
        },
        [index, slides.length, onIndexChange],
    );

    // Escape closes, the arrow keys page, and +/- zoom — the overlay covers
    // the page, so it owns the keyboard while it is up.
    //
    // Except where something on top of it owns the keyboard instead: the
    // design-board picker takes typed characters into its search and naming
    // fields, and its own Escape closes it rather than the viewer behind it.
    useEffect(() => {
        const onKey = (event: KeyboardEvent) => {
            if (pickerOpen || isTypingTarget(event.target)) return;
            if (event.key === 'Escape') onClose();
            if (event.key === 'ArrowLeft') step(-1);
            if (event.key === 'ArrowRight') step(1);
            if (event.key === '+' || event.key === '=') zoomBy(ZOOM_STEP);
            if (event.key === '-') zoomBy(-ZOOM_STEP);
        };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [onClose, step, zoomBy, pickerOpen]);

    /**
     * Focus goes into the viewer when it opens and back to whatever opened it
     * when it closes.
     *
     * The dialog itself takes it rather than the first control: a Tab from
     * there reaches Close, and focusing a button here would draw a ring the
     * reader never asked for when they opened the viewer with the mouse.
     */
    useEffect(() => {
        const opener =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        dialogRef.current?.focus();
        return () => opener?.focus();
    }, []);

    /**
     * Tab stays inside the viewer while it is up. `aria-modal` takes the page
     * behind out of the screen reader's tree but leaves it in the tab order,
     * so without this the reader tabs out into the grid behind and cannot
     * reach Close, the arrows or Save again.
     *
     * Stood down while the board picker is open: it is a popover anchored
     * outside this tree, and holding focus in here would pull the reader back
     * out of the fields they were sent to.
     */
    useEffect(() => {
        if (pickerOpen) return;
        const onTab = (event: KeyboardEvent) => {
            const dialog = dialogRef.current;
            if (event.key !== 'Tab' || !dialog) return;
            const stops = tabStopsIn(dialog);
            const first = stops[0];
            const last = stops[stops.length - 1];
            if (!first || !last) return;

            // Only while the reader is in here: a layer opened over the viewer
            // — the sign-in gate a guest's save raises — takes focus out of
            // this tree, and pulling it back would make that layer unusable.
            const active = document.activeElement;
            if (!(active instanceof Node) || !dialog.contains(active)) return;
            if (event.shiftKey && (active === first || active === dialog)) {
                event.preventDefault();
                last.focus();
                return;
            }
            if (!event.shiftKey && active === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', onTab);
        return () => document.removeEventListener('keydown', onTab);
    }, [pickerOpen]);

    // The page behind must not scroll under the overlay — and the chrome that
    // belongs to it (Back to top) has nothing to offer while a full-screen
    // viewer is up, so the overlay says it is open and that control stands
    // down. An attribute rather than a context: the chrome is mounted by the
    // page, far outside this tree.
    useEffect(() => {
        const previous = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        document.documentElement.dataset.imageViewer = 'open';
        return () => {
            document.body.style.overflow = previous;
            delete document.documentElement.dataset.imageViewer;
        };
    }, []);

    const slide = targetReady ? target : (held ?? target);

    // What Share offers: the page, with the photograph on screen as the
    // picture to pin. Absolute, because the pin is taken off-site.
    const shareOptions: ShareOptions | null =
        slide && typeof window !== 'undefined'
            ? {
                  url: window.location.href,
                  title,
                  image: new URL(slide.src, window.location.origin).href,
              }
            : null;

    /**
     * A phone hands Share to its own sheet, which is where its reader keeps
     * the apps they send things to — the same choice the listing tiles make.
     * Everywhere else the site's own sheet drops from the pill, so the viewer
     * shares the way every other Share on the site does rather than through
     * whatever the browser happens to offer.
     */
    const shareOnPhone = () => {
        if (!shareOptions) return;
        if (isNativeShareAvailable()) {
            void shareNative(shareOptions);
            return;
        }
        void navigator.clipboard?.writeText(shareOptions.url);
    };

    if (!slide) return null;

    const aspect = ratios[slide.src] || null;

    /**
     * The arrows centre on the photograph, not on the stage's box. At rest the
     * stage carries 5.5rem of foot padding to balance the top bar, and `top`
     * measures against the padded box — half-way down it sits half that
     * padding below the picture's middle. Zoomed, the padding is gone.
     */
    const arrowY =
        zoom === ZOOM_MIN ? 'md:top-[calc(50%-2.75rem)]' : 'md:top-1/2';

    // Every tag placed on the photograph keeps its mark, so the reader can see
    // what is tagged before reaching for the list; hovering an item in the list
    // enlarges its own mark.
    const markedTags = (slide.tags ?? []).filter(
        (tag) => tag.positionX != null && tag.positionY != null,
    );

    /**
     * The photograph, and the mark over it.
     *
     * Wrapped so the mark has the rendered image to position against: the
     * stage fills the screen and the picture is contained inside it, so a
     * percentage against the stage would land in the empty margin beside a
     * portrait shot rather than on the image.
     */
    const picture = slide.video ? (
        // A size container, so the box inside can be fitted to the stage in
        // both axes at once. `max-height` alone cannot do it: it clamps the
        // height of a ratio box without taking the width back, which left the
        // player wider than sixteen by nine and its own ground showing as
        // bands either side of the picture rather than the viewer's backdrop.
        <span
            className="relative flex size-full items-center justify-center self-stretch"
            style={{ containerType: 'size' }}
        >
            <span
                // Sixteen by nine, the shape every embed this page plays comes
                // back at. Given the box up front so the player does not
                // resize the stage under the reader once it loads; the width
                // yields to the stage's height whenever that is the tighter
                // of the two, which is what keeps the box exactly the shape of
                // the video.
                className="relative inline-flex overflow-hidden rounded-tile"
                style={{
                    aspectRatio: '16 / 9',
                    width: 'min(100%, calc(100cqh * 16 / 9))',
                }}
            >
                {slide.video}
            </span>
        </span>
    ) : (
        <span
            // The wrapper carries the shape, not the image: it is a flex item
            // of the stage and was being stretched in both axes, which made
            // `max-width` and `max-height` bind independently and left the box
            // a different rectangle from the photograph. `self-center` stops
            // the stretch and the measured ratio gives it the picture's own
            // shape, so the caps then fit it to the stage. The image fills
            // that exactly — `cover` and `contain` are the same thing once the
            // box and the picture agree.
            className={cn(
                'relative inline-flex max-h-full max-w-full self-center',
                // The clear-cut ground goes on the picture's own box, never on
                // the backdrop: the fill is what the cut-out product is meant
                // to sit on, and spread over the whole screen it would just
                // replace the darkened page with a grey one.
                slide.multiplied &&
                    'rounded-tile bg-[var(--color-clearcut-fill)]',
            )}
            // Fitted to the stage, not left at the file's own pixel size: the
            // widest box of the photograph's shape that fits the stage in both
            // axes, read off the stage's size through container units.
            style={
                aspect
                    ? {
                          aspectRatio: aspect,
                          width: `min(100cqw, calc(100cqh * ${aspect}))`,
                      }
                    : undefined
            }
        >
            <img
                key={slide.src}
                ref={imageRef}
                onLoad={(event) => measure(event.currentTarget)}
                src={slide.src}
                alt={slide.alt}
                // Without this a drag on a zoomed image is a native image
                // drag — the browser lifts a translucent ghost of the
                // photograph — instead of the pan the stage is listening for.
                draggable={false}
                className={cn(
                    'max-h-full max-w-full rounded-tile select-none',
                    // Only on first open, before anything is ready to hold:
                    // hidden rather than drawn at the file's own size.
                    !(slide.src in ratios) && 'opacity-0',
                    aspect ? 'size-full object-cover' : 'object-contain',
                    slide.multiplied && 'mix-blend-multiply',
                )}
            />
            {markedTags.map((tag) => (
                <span
                    key={tag.id}
                    aria-hidden="true"
                    className={cn(
                        'pointer-events-none absolute size-[0.875rem] -translate-x-1/2 -translate-y-1/2 rounded-full bg-white',
                        // A ring of shadow rather than a border: the mark has
                        // to read on a pale wall as well as a dark floor.
                        'shadow-[0_0_0_0.1875rem_rgba(0,0,0,0.35)]',
                        'transition-transform duration-200 motion-reduce:transition-none',
                        tag.id === hoveredTagId && 'scale-[1.45]',
                    )}
                    style={{
                        left: `${tag.positionX}%`,
                        top: `${tag.positionY}%`,
                    }}
                />
            ))}
        </span>
    );

    const overlay = (
        <div
            ref={dialogRef}
            role="dialog"
            aria-modal="true"
            aria-label={title}
            // Focusable only to script: it is where focus lands on open, so
            // the first Tab carries on into the chrome below it.
            tabIndex={-1}
            className={cn(
                'fixed inset-0 z-[200] flex flex-col',
                // No ring on the ground itself: it is a landing spot for
                // script, never a stop the reader tabs to, and the controls
                // inside it carry their own.
                'focus-visible:outline-hidden',
                OVERLAY_GROUND,
                // The ground here is the viewer's own, not the host page's,
                // and a portal starts from the light default — so the dark
                // ring token is set here rather than inherited.
                'focus-ring-on-dark',
                'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                entering ? 'opacity-0' : 'opacity-100',
            )}
        >
            {/* At rest the bar is a row of the column, so the stage starts
                below it and the photograph centres in what is left. Zoomed, it
                lifts out of the flow onto the image — the reader wants the
                pixels, and the bar giving its height back is what lets the
                photograph reach the top edge the way it already reaches the
                bottom. The controls carry their own surfaces, so they still
                read against whatever is behind them. */}
            <div
                className={cn(
                    OVERLAY_TOP_BAR,
                    // Three columns on desktop, the outer two equal, so the
                    // credit sits on the screen's centre line and gives way to
                    // the controls rather than running under them.
                    'md:grid md:grid-cols-[1fr_minmax(0,auto)_1fr] md:gap-[1.5rem]',
                    zoom === ZOOM_MIN ? '' : 'absolute inset-x-0 top-0 z-10',
                )}
            >
                <button
                    type="button"
                    aria-label="Close"
                    onClick={onClose}
                    className={cn(WHITE_DISC, 'md:justify-self-start')}
                >
                    <X className="size-[1.25rem]" />
                </button>

                {slide.credit ? (
                    <div className="flex min-w-0 flex-col items-center text-center text-[1.375rem] leading-[1.15] max-md:hidden">
                        <CreditLine
                            text={slide.credit.title}
                            href={slide.credit.titleLink}
                            className="max-w-full truncate font-medium text-white"
                        />
                        {slide.credit.subtitle ? (
                            <CreditLine
                                text={slide.credit.subtitle}
                                href={slide.credit.subtitleLink}
                                className="max-w-full truncate text-white/60"
                            />
                        ) : null}
                    </div>
                ) : (
                    <span className="max-md:hidden" />
                )}

                <div className="flex items-center max-md:gap-2 md:gap-[0.75rem] md:justify-self-end">
                    {mobile || !shareOptions ? (
                        <button
                            type="button"
                            onClick={shareOnPhone}
                            className={PILL}
                        >
                            Share
                            <Share className="size-[1rem]" />
                        </button>
                    ) : (
                        <ShareSheet share={shareOptions}>
                            {({ open, toggle }) => (
                                <button
                                    type="button"
                                    aria-haspopup="menu"
                                    aria-expanded={open}
                                    onClick={toggle}
                                    className={PILL}
                                >
                                    Share
                                    <Share className="size-[1rem]" />
                                </button>
                            )}
                        </ShareSheet>
                    )}

                    {/* Where the credit names a practice with a page of its
                        own, the pill leads there; a detail page, where the
                        reader is already on the practice's work, keeps the
                        board it would save to. */}
                    {slide.credit?.subtitleLink ? (
                        <Link
                            to={slide.credit.subtitleLink}
                            className={cn(
                                PILL,
                                'whitespace-nowrap no-underline max-md:hidden',
                            )}
                        >
                            View Profile
                        </Link>
                    ) : (
                        <button
                            type="button"
                            onClick={openFollowSelector}
                            className={cn(PILL, 'max-md:hidden')}
                        >
                            {boardName}
                        </button>
                    )}

                    <button
                        type="button"
                        onClick={openFollowSelector}
                        className={cn(
                            CHROME,
                            'h-[3rem] bg-white px-[1.5rem] text-[1rem] text-[#181818] hover:bg-white/85',
                        )}
                    >
                        Save
                    </button>
                </div>
            </div>

            {/* The stage sits below the top bar, so centring inside it puts the
                image below the middle of the screen. The foot carries the bar's
                own height back — 48px of control plus its 20px padding either
                side — so the photograph centres against the viewport. */}
            <div
                className={cn(
                    'relative flex flex-1 items-center justify-center overflow-hidden max-md:px-4 md:px-[4.5rem]',
                    // The foot only has to balance the top bar while the image
                    // is at rest and centring matters. Zoomed in, the reader
                    // wants the pixels, so it gives the height back and the
                    // photograph runs to both edges.
                    zoom === ZOOM_MIN
                        ? 'max-md:pb-[4.5rem] md:pb-[5.5rem]'
                        : 'pb-0',
                )}
            >
                <div
                    ref={stageRef}
                    onPointerDown={onPanStart}
                    onPointerMove={onPanMove}
                    onPointerUp={onPanEnd}
                    onPointerCancel={onPanEnd}
                    onClickCapture={onStageClickCapture}
                    className={cn(
                        '[container-type:size] flex size-full items-center justify-center select-none',
                        // Only while a gesture of ours is driving: the zoom
                        // pan, or the swipe that turns the page. Left on
                        // unconditionally it also took the browser's own
                        // pinch-zoom off the photograph, at every width —
                        // including a touchscreen laptop or an iPad, where
                        // neither gesture is bound.
                        (canPan || canTurn) && 'touch-none',
                        // The transition is for the zoom step and the opening
                        // move; a drag has to track the pointer exactly, so it
                        // is turned off while one is under way.
                        !dragFrom.current &&
                            'transition-transform duration-300 ease-out motion-reduce:transition-none',
                        canPan && 'cursor-grab active:cursor-grabbing',
                    )}
                    style={{
                        transform: entering
                            ? fromTransform
                            : `translate3d(${pan.x}px, ${pan.y}px, 0) scale(${zoom})`,
                    }}
                >
                    {/* Sized by max-width/height rather than filling the stage:
                        a contained image leaves transparent margins inside its
                        own box, so a corner set on that box would round empty
                        space instead of the photograph.

                        The link, when there is one, wraps the picture rather
                        than the stage: the stage fills the screen, and a link
                        over the whole of it would swallow every click meant
                        for the ground behind. */}
                    {slide.href ? (
                        <Link
                            to={slide.href}
                            aria-label={`View ${slide.credit?.title ?? title}`}
                            className={cn(
                                'flex max-h-full max-w-full',
                                // A video fits itself to the stage through a
                                // size container, and a size container whose
                                // own height resolves to `auto` measures zero
                                // — its contents are not allowed to size it.
                                // The link is height-auto like any flex item
                                // under `items-center`, so the video branch
                                // needs the stage's height passed through it;
                                // a picture sizes from its own box and must
                                // not be stretched.
                                slide.video && 'h-full',
                            )}
                        >
                            {picture}
                        </Link>
                    ) : (
                        picture
                    )}
                </div>

                {/* The arrows are the desktop's; the phone turns the page by
                    swiping the photograph or tapping its edges, and says where
                    it is with the dots on the foot's line instead. */}
                {slides.length > 1 ? (
                    <>
                        <button
                            type="button"
                            aria-label="Previous image"
                            onClick={() => step(-1)}
                            className={cn(
                                DISC,
                                arrowY,
                                'absolute -translate-y-1/2 max-md:hidden md:left-[1.5rem]',
                            )}
                        >
                            <ChevronLeft className="size-[1.5rem]" />
                        </button>
                        <button
                            type="button"
                            aria-label="Next image"
                            onClick={() => step(1)}
                            className={cn(
                                DISC,
                                arrowY,
                                'absolute -translate-y-1/2 max-md:hidden md:right-[1.5rem]',
                            )}
                        >
                            <ChevronRight className="size-[1.5rem]" />
                        </button>
                    </>
                ) : null}

                {websiteURL ? (
                    /*
                     * Bottom-left, the corner every tile's action panel already
                     * puts it in, so the viewer answers the same question the
                     * tile it opened from did.
                     *
                     * A button rather than an anchor: this matches the panel's
                     * tracked action, which reports the referral before opening
                     * the tab. On a phone it sits 50 off the foot of the
                     * screen, with the credit block above it.
                     */
                    <div className="absolute z-10 max-md:bottom-[50px] max-md:left-4 md:bottom-[1.5rem] md:left-[1.5rem]">
                        <button
                            type="button"
                            onClick={onVisitWebsite}
                            className={cn(
                                'inline-flex h-[3rem] cursor-pointer items-center gap-[0.375rem] rounded-[1.375rem] px-[1.25rem]',
                                'bg-white text-[1rem] font-medium text-[#181818] transition-colors hover:bg-white/85',
                            )}
                        >
                            <ArrowUpRight
                                aria-hidden="true"
                                className="size-4"
                            />
                            Visit site
                        </button>
                    </div>
                ) : null}

                {slide.credit ? (
                    // On a phone the top bar has no room for it, so the credit
                    // sits over the controls' row at the foot — the row's 50
                    // off the screen's foot, its 3rem, and a rem of air; on
                    // desktop it is in the top bar.
                    <div className="absolute right-4 bottom-[calc(50px+3rem+1rem)] left-4 flex flex-col gap-[0.25rem] text-[1.125rem] leading-[1.15] md:hidden">
                        <CreditLine
                            text={slide.credit.title}
                            href={slide.credit.titleLink}
                            className="font-medium text-white"
                        />
                        {slide.credit.subtitle ? (
                            <CreditLine
                                text={slide.credit.subtitle}
                                href={slide.credit.subtitleLink}
                                className="text-white/60"
                            />
                        ) : null}
                    </div>
                ) : null}

                {slide.tags?.length ? (
                    // Down the right of the photograph, clear of the zoom
                    // discs in the corner below them.
                    <ul className="absolute flex scrollbar-elegant flex-col gap-[0.25rem] overflow-y-auto max-md:hidden md:top-[1.5rem] md:right-[1.5rem] md:max-h-[calc(100%-10rem)] md:w-[20rem]">
                        {slide.tags.map((tag) => (
                            <li
                                key={tag.id}
                                onMouseEnter={() => setHoveredTagId(tag.id)}
                                onMouseLeave={() => setHoveredTagId(null)}
                            >
                                <Link
                                    to={tag.link}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex h-[5.875rem] items-center gap-[1.3125rem] rounded-[0.375rem] bg-[rgba(250,250,250,0.7)] p-[0.375rem] shadow-[inset_0_0_0.25rem_0_rgba(0,0,0,0.25)] backdrop-blur-[5px] transition-colors hover:bg-[rgba(250,250,250,0.9)]"
                                >
                                    {tag.imageUrl ? (
                                        <img
                                            src={tag.imageUrl}
                                            alt={tag.title}
                                            className="size-[5.125rem] shrink-0 rounded-[0.25rem] object-cover"
                                        />
                                    ) : (
                                        <span className="size-[5.125rem] shrink-0 rounded-[0.25rem] bg-[#e8e8e8]" />
                                    )}
                                    <span className="flex min-w-0 flex-col gap-[0.0625rem] pr-3">
                                        <span className="truncate text-[1rem] leading-[1.25] font-medium text-[#141414]">
                                            {tag.title}
                                        </span>
                                        <span className="truncate text-[1rem] leading-[1.25] text-[#737373]">
                                            {tag.professional}
                                        </span>
                                    </span>
                                </Link>
                            </li>
                        ))}
                    </ul>
                ) : null}

                {isVideo ? null : (
                    // On a phone, side by side on Visit site's line — the same
                    // 3rem height, the same foot — so the viewer's controls
                    // read as one row; desktop stacks them in the corner.
                    <div className="absolute flex max-md:right-4 max-md:bottom-[50px] max-md:flex-row max-md:gap-2 md:right-[1.5rem] md:bottom-[1.5rem] md:flex-col md:gap-[0.75rem]">
                        <button
                            type="button"
                            aria-label="Zoom in"
                            disabled={zoom >= ZOOM_MAX}
                            onClick={() => zoomBy(ZOOM_STEP)}
                            className={cn(WHITE_DISC, 'disabled:opacity-40')}
                        >
                            <Plus className="size-[1.25rem]" />
                        </button>
                        <button
                            type="button"
                            aria-label="Zoom out"
                            disabled={zoom <= ZOOM_MIN}
                            onClick={() => zoomBy(-ZOOM_STEP)}
                            className={cn(WHITE_DISC, 'disabled:opacity-40')}
                        >
                            <Minus className="size-[1.25rem]" />
                        </button>
                    </div>
                )}

                {slides.length > 1 ? (
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-x-0 bottom-[50px] flex h-[3rem] items-center justify-center gap-[0.375rem] md:hidden"
                    >
                        {slides.map((dot, at) => (
                            <span
                                key={`${dot.src}-${at}`}
                                className={cn(
                                    'size-[0.375rem] rounded-full bg-white transition-opacity',
                                    at === index ? 'opacity-100' : 'opacity-40',
                                )}
                            />
                        ))}
                    </div>
                ) : null}
            </div>

            {followSelector}
        </div>
    );

    // Out to the body, rather than left where the page opened it. A viewer
    // that covers the window must not be scoped by the layer it was opened
    // from: the project page lifts everything under its banner onto a `z-10`
    // band (so the backdrop mark sits behind the page), and inside that
    // stacking context this dialog's own z-index counts for nothing against
    // the page's sticky CTA cluster above it — the cluster drew over the
    // photograph. The body is the one parent that imposes no ceiling.
    return typeof document === 'undefined'
        ? null
        : createPortal(overlay, document.body);
};
